import { useState } from 'react';
import type { VavClient } from '@vav/client-core';
import { useT } from '../i18n.tsx';

interface Props {
  client: VavClient;
  userId: string;
  name: string;
  onClose: () => void;
}

/**
 * The out-of-band check that turns "the server says this is Dana" into "Dana
 * and I read the same 60 digits aloud". Without this step, end-to-end
 * encryption still trusts the server's word on who owns which key.
 */
export function SafetyNumberDialog({ client, userId, name, onClose }: Props) {
  const t = useT();
  const number = client.safetyNumberWith(userId);
  const contact = client.getContact(userId);
  const [verified, setVerified] = useState(Boolean(contact?.verified));

  async function toggle() {
    const next = !verified;
    setVerified(next);
    await client.markVerified(userId, next);
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label={t('safety.title')}>
        <header className="dialog__header">
          <h2>{t('safety.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('invite.close')}>
            ✕
          </button>
        </header>

        {number ? (
          <>
            <p className="muted">
              {t('safety.compare', { name })}
            </p>
            <pre className="safetyNumber">{number}</pre>
            <label className="checkbox">
              <input type="checkbox" checked={verified} onChange={() => void toggle()} />
              {t('safety.confirm')}
            </label>
            <p className="hint">
              {t('safety.changeNote', { name })}
            </p>
          </>
        ) : (
          <p className="muted">
            {t('safety.noSession', { name })}
          </p>
        )}
      </div>
    </div>
  );
}
